import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from '@/store';
import { loginErrorSelector, loginPendingSelector } from '@/store/selectors/auth';
import { reset, signInUser } from '@/store/slices/auth';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm } from 'react-hook-form';
import { IMAGES } from '@/assets';
import { Link } from 'react-router-dom';
import Spinner from '@/components/Spinner/Spinner';
import { Shape } from '@/types/Forms';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

type LoginFormState = {
  email: string;
  password: string;
};

const schema = yup.object<Shape<LoginFormState>>().shape({
  email: yup
    .string()
    .trim()
    .email(`Email format is not correct`)
    .required(`Email is mandatory`)
    .nullable(),
  password: yup
    .string()
    .trim()
    .min(8, 'Password length must be at least 8 characters')
    .max(32, 'Password length must be less than 32 characters')
    .required(`Password is mandatory`)
    .nullable()
});

function Login() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: null,
      password: null
    }
  });

  function onSubmitHandler({ email, password }: FieldValues) {
    dispatch(signInUser({ email, password }));
    resetForm();
  }

  const loginPending = useSelector(loginPendingSelector);
  const loginError = useSelector(loginErrorSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    resetForm();
    dispatch(reset());
  }, [dispatch, resetForm]);

  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      <div
        className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        data-cy="login">
        <div className="w-full max-w-md space-y-8">
          <div>
            <img className="mx-auto h-12 w-auto" src={IMAGES.logo} alt="Your Company" />
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email and password to start using Delpo Q.A.
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmitHandler)}>
            <input type="hidden" name="remember" value="true" />
            <div className="-space-y-px rounded-md shadow-sm">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  {...register('email')}
                  className="relative block w-full appearance-none rounded-none rounded-t-md border border-gray-300 px-3
                  py-2 text-gray-900
                  placeholder-gray-500 focus:z-10 focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                  type="email"
                  id="email"
                  aria-label="email"
                  placeholder="Email address"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  required
                />
                <p data-testid="email-error" className="text-xs	text-danger">
                  {!loginPending && errors.email?.message?.toString()}
                </p>
              </div>
              <div className="relative">
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  {...register('password')}
                  className="relative block w-full appearance-none rounded-none rounded-b-md border border-gray-300 px-3
                  py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                  type={showPassword ? 'text' : 'password'}
                  aria-invalid={errors.password ? 'true' : 'false'}
                  id="password"
                  data-testid="password"
                  placeholder="Password"
                  required
                />
                <div className="absolute right-0 top-0 z-10 mt-2 mr-3">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 focus:text-gray-500 focus:outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="toggle password visibility">
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p data-testid="password-error" className="text-xs text-danger">
                  {!loginPending && errors.password?.message?.toString()}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link to="forgot-password" className="font-medium text-primary hover:text-primary">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-primary py-2 px-4
                text-sm font-medium text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="submit"
                data-testid="login"
                type="submit">
                Login
                {loginPending ? <Spinner /> : null}
              </button>
              {loginError && (
                <p data-testid="api-error" className="mt-4 text-xs text-danger">
                  {loginError === 'auth/invalid-credential' || loginError === 'auth/wrong-password'
                    ? 'Invalid credentials. Please try again or use change password.'
                    : 'Unable to sign in. Please try again.'}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default Login;
